import { usePreferences } from "@/hooks/usePreferences";
import "./OperatorPreferencesPage.css";

const STEP_TYPES = [
  { value: "", label: "Any" },
  { value: "scan", label: "Scan" },
  { value: "classify", label: "Classify" },
  { value: "key", label: "Key" },
  { value: "verify", label: "Verify" },
  { value: "qc", label: "QC" },
  { value: "supervisor", label: "Supervisor" },
  { value: "export", label: "Export" },
];

const ZOOM_OPTIONS = [
  { value: 0, label: "Auto" },
  { value: 1, label: "1x" },
  { value: 2, label: "2x" },
  { value: 3, label: "3x" },
];

export function OperatorPreferencesPage() {
  const { preferences, isLoading, isSaving, updatePreference } = usePreferences();

  if (isLoading) {
    return (
      <div className="sp-page form-default">
        <div className="sp-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="sp-page form-default">
      <div className="sp-header">
        <h1 className="sp-title">Operator Preferences</h1>
        {isSaving && <span className="sp-saving">Saving...</span>}
      </div>

      <div className="sp-content">
        {/* Keying Behavior */}
        <section className="sp-section">
          <h2 className="sp-section-title">Keying Behavior</h2>

          {/* F-CAPTURE-044 — no skip-threshold control here: founder ruling, it is an admin
              setting per form/job type (verifyThreshold), not an operator preference. */}
          <div className="sp-field">
            <label className="sp-toggle">
              <input
                type="checkbox"
                checked={preferences.autoFocusMode}
                onChange={(e) => updatePreference("autoFocusMode", e.target.checked)}
              />
              <span className="sp-toggle-label">Auto focus mode</span>
            </label>
            <span className="sp-hint">
              Automatically enter focus mode when &gt;70% of fields are auto-verified
            </span>
          </div>

          <div className="sp-field">
            <label className="sp-toggle">
              <input
                type="checkbox"
                checked={preferences.defaultFocusMode}
                onChange={(e) => updatePreference("defaultFocusMode", e.target.checked)}
              />
              <span className="sp-toggle-label">Default focus mode</span>
            </label>
            <span className="sp-hint">
              Start every document in focus mode (only show fields needing attention)
            </span>
          </div>
        </section>

        {/* Audio */}
        <section className="sp-section">
          <h2 className="sp-section-title">Audio</h2>

          <div className="sp-field">
            <label className="sp-toggle">
              <input
                type="checkbox"
                checked={preferences.audioEnabled}
                onChange={(e) => updatePreference("audioEnabled", e.target.checked)}
              />
              <span className="sp-toggle-label">Audio feedback</span>
            </label>
          </div>

          <div className="sp-field">
            <label className="sp-label" htmlFor="audio-volume">
              Volume
            </label>
            <div className="sp-slider-row">
              <input
                id="audio-volume"
                type="range"
                min={0}
                max={1}
                step={0.1}
                value={preferences.audioVolume}
                onChange={(e) =>
                  updatePreference("audioVolume", parseFloat(e.target.value))
                }
                className="sp-slider"
                disabled={!preferences.audioEnabled}
              />
              <span className="sp-slider-value">
                {Math.round(preferences.audioVolume * 100)}%
              </span>
            </div>
          </div>
        </section>

        {/* Display */}
        <section className="sp-section">
          <h2 className="sp-section-title">Display</h2>

          <div className="sp-field">
            <span className="sp-label">Font size</span>
            <div className="sp-radio-group">
              {(["small", "medium", "large"] as const).map((size) => (
                <label key={size} className="sp-radio">
                  <input
                    type="radio"
                    name="fontSize"
                    value={size}
                    checked={preferences.fontSize === size}
                    onChange={() => updatePreference("fontSize", size)}
                  />
                  <span className="sp-radio-label">
                    {size.charAt(0).toUpperCase() + size.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="sp-field">
            <label className="sp-toggle">
              <input
                type="checkbox"
                checked={preferences.showSnippetPanel}
                onChange={(e) => updatePreference("showSnippetPanel", e.target.checked)}
              />
              <span className="sp-toggle-label">Snippet panel visible</span>
            </label>
          </div>

          <div className="sp-field">
            <label className="sp-toggle">
              <input
                type="checkbox"
                checked={preferences.showSessionTimer}
                onChange={(e) => updatePreference("showSessionTimer", e.target.checked)}
              />
              <span className="sp-toggle-label">Session timer visible</span>
            </label>
          </div>

          <div className="sp-field">
            <label className="sp-label" htmlFor="snippet-zoom">
              Default snippet zoom
            </label>
            <select
              id="snippet-zoom"
              className="sp-select"
              value={preferences.snippetZoomDefault}
              onChange={(e) =>
                updatePreference("snippetZoomDefault", parseInt(e.target.value, 10))
              }
            >
              {ZOOM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        {/* Queue Preferences */}
        <section className="sp-section">
          <h2 className="sp-section-title">Queue Preferences</h2>

          <div className="sp-field">
            <label className="sp-label" htmlFor="preferred-step">
              Preferred step type
            </label>
            <select
              id="preferred-step"
              className="sp-select"
              value={preferences.preferredStepType ?? ""}
              onChange={(e) =>
                updatePreference(
                  "preferredStepType",
                  e.target.value || null,
                )
              }
            >
              {STEP_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </section>
      </div>
    </div>
  );
}
